// External libraries
import { Component } from "react";

// FontAwesome icons
import { faEraser } from "@fortawesome/pro-duotone-svg-icons";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { RadioGroup } from "@progress/kendo-react-inputs";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { ColorChangingIcon } from "../../Common/UI/ColorChangingIcon";
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { validators } from "../../Form/validators";
import { Invisible, validateStageStep } from "../../Utilities/EntryUtils";

// Signals
import { allDatasets } from "../../Common/Store/CommonSignals";
import {
  clearEntryFields,
  disabledSections,
  entryValues,
  formErrors,
  readOnlyEntry,
  template,
} from "../../Entry/Manage/EditEntrySignals";

export class RadioButtonField extends Component {
  constructor({
    field,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    labelVisible,
    eventIndex,
    value,
    galleryParentName,
    isStageField,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties } = field;
    this.state = {
      value: value,
      valid: this.validate(value, properties?.IsRequired),
      invisible: false,
      disabled: false,
      parentFieldValue: entryValues.value[field?.properties?.["ParentFieldName"]],
      options: this.optionsHelper(
        field?.properties?.["ParentFieldName"],
        field?.properties?.["Dataset"]?.id
      ),
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.datasetId = properties?.Dataset?.id;
    this.parentFieldName = properties?.ParentFieldName;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.isRequired = properties?.IsRequired;
    this.isReadOnly = properties?.IsReadOnly;
    this.visibilityRules = properties?.VisibilityRules;
    this.parameterValues = properties?.ParameterValues;
    this.defaultValue = properties?.DefaultValue;
    this.enableClear = properties?.EnableClear;
    this.horizontalDisplay = properties?.HorizontalDisplay;
    this.labelVisible = labelVisible;
    this.label = properties?.Label || `${fieldName}:`;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.handleFieldChange = handleFieldChange;
    this.galleryParentName = galleryParentName;
    this.isStageField = isStageField;
    this.isPrintPreview = isPrintPreview;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.validationMessage = "Error: An option is required.";
    this.disposeValidationEffect = () => {};
    this.disposeDependencyEffect = () => {};
    this.disposeClearFieldsEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  //#region lifecycle functions
  componentDidMount() {
    this.handleClearFieldEffect();
    if (this.isRequired) {
      if (!this.validate(this.state.value, this.isRequired)) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
      this.handleValidationEffect();
      this.updateFormErrors();
    }
    if (this.visibilityRules || this.parentFieldName) this.handleDependencyEffect();

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
  }

  componentWillUnmount() {
    this.disposeValidationEffect();
    this.disposeDependencyEffect();
    this.disposeClearFieldsEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      this.state.value !== nextState.value ||
      this.state.valid !== nextState.valid ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled ||
      this.state.parentFieldValue !== nextState.parentFieldValue
    );
  }

  getSnapshotBeforeUpdate(_, prevState) {
    return {
      visibilityChange: prevState.invisible && !this.state.invisible,
    };
  }

  componentDidUpdate(_, __, snapshot) {
    if (snapshot.visibilityChange) this.setDefaultValue();
    if (this.state.invisible) this.clearField();
    if (!this.state.invisible && this.isRequired) {
      this.setState(
        (prevState) => ({ valid: this.validate(prevState.value, this.isRequired) }),
        () => {
          this.updateFormErrors();
        }
      );
    }
    if (this.state.disabled) this.clearFormErrors();
  }
  //#endregion lifecycle functions

  //#region custom functions
  validate(value, isRequired) {
    return isRequired ? validators.required(value) : true;
  }

  handleClearFieldEffect() {
    this.disposeClearFieldsEffect = effect(() => {
      if (clearEntryFields.value) {
        if (this.defaultValue) {
          if (entryValues.value[this.fieldName] !== this.defaultValue) {
            this.clearField();
            this.setDefaultValue();
          }
        } else if (entryValues.value[this.fieldName]) {
          this.clearField();
        }
      }
    });
  }

  handleValidationEffect() {
    this.disposeValidationEffect = effect(() => {
      this.setState({
        valid: formErrors.value[this.sectionName]
          ? formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1
          : true,
      });
    });
  }

  updateFormErrors() {
    if (!this.state.valid) {
      if (
        formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1 ||
        !formErrors.value[this.sectionName]
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
    } else {
      this.clearFormErrors();
    }
  }

  clearFormErrors() {
    const newFormErrors = { ...formErrors.value };
    const sectionErrors = newFormErrors[this.sectionName];
    if (sectionErrors) {
      newFormErrors[this.sectionName] = sectionErrors.filter((error) => error !== this.fieldName);
    }
    formErrors.value = newFormErrors;
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      const newOptions = this.optionsHelper(this.parentFieldName, this.datasetId);
      const newParentFieldValue = this.galleryParentName
        ? entryValues.value?.[this.galleryParentName]?.[this.eventIndex]?.[this.parentFieldName]
        : entryValues.value[this.parentFieldName];

      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
        options: newOptions,
      });

      if (newParentFieldValue !== this.state.parentFieldValue) {
        this.setState(
          (prevState) => ({
            parentFieldValue: newParentFieldValue,
            // Only clear the value if it's no longer valid and not intentionally set to empty
            value:
              newOptions.includes(prevState.value) || prevState.value === "" ? prevState.value : "",
          }),
          () => {
            if (this.state.value === "") {
              this.onChange({ value: "" });
            }
          }
        );
      }
    });
  }

  clearField() {
    this.clearFormErrors();
    this.setState({ value: "" });
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: "" },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  }

  setDefaultValue() {
    if (this.defaultValue) {
      this.setState({
        value: this.defaultValue,
      });
      this.handleFieldChange({
        e: { target: { name: this.fieldName }, value: this.defaultValue },
        sectionIndex: this.sectionIndex,
        fieldIndex: this.fieldIndex,
        eventIndex: this.eventIndex,
      });
    }
  }

  onChange(e) {
    if (!this.isPrintPreview) {
      const newValue = e.value ?? ""; // Treat null as empty string
      this.setState(
        {
          valid: this.validate(newValue, this.isRequired),
          value: newValue,
        },
        () => {
          this.updateFormErrors();
          this.handleFieldChange({
            e: { ...e, target: { ...e.target, name: this.fieldName, value: newValue } },
            sectionIndex: this.sectionIndex,
            fieldIndex: this.fieldIndex,
            fieldType: this.fieldType,
            eventIndex: this.eventIndex,
          });
          if (this.isStageField) validateStageStep();
        }
      );
    }
  }

  onBlur() {
    this.setState(
      (prevState) => ({
        valid: this.validate(prevState.value, this.isRequired),
      }),
      () => {
        this.updateFormErrors();
        if (this.isStageField) validateStageStep();
      }
    );
  }

  resetRadioGroup(e) {
    this.setState(
      {
        valid: this.validate("", this.isRequired),
        value: "",
      },
      () => {
        this.updateFormErrors();
        this.handleFieldChange({
          e: { ...e, target: { ...e.target, name: this.fieldName, value: "" } },
          sectionIndex: this.sectionIndex,
          fieldIndex: this.fieldIndex,
          fieldType: this.fieldType,
          eventIndex: this.eventIndex,
        });
        if (this.isStageField) validateStageStep();
      }
    );
  }

  optionsHelper(parentFieldName, datasetId) {
    const parentFieldValue = this.galleryParentName
      ? entryValues.value?.[this.galleryParentName]?.[this.eventIndex]?.[parentFieldName]
      : entryValues.value[parentFieldName];

    if (parentFieldName) {
      return allDatasets.value
        .find((x) => x.id === datasetId)
        ?.values.filter((option) => option.filterValue === parentFieldValue)
        .map((option) => option.value);
    } else {
      return allDatasets.value
        .find((x) => x.id === datasetId)
        ?.values?.map((option) => option.value);
    }
  }
  //#endregion custom functions

  render() {
    if (this.state.invisible) return null;
    return (
      <Ripple>
        <div className="k-d-flex k-flex-col k-font-size-lg !k-font-weight-semibold">
          <div className="k-d-flex k-align-items-center k-gap-2">
            {this.labelVisible && (
              <Label
                id={this.fieldName + "_label"}
                style={{ fontSize: "14px", fontWeight: "600" }}
              >
                {this.label}
              </Label>
            )}
            {this.enableClear && this.state.value && !this.isPrintPreview && (
              <ColorChangingIcon
                icon={faEraser}
                initialPrimaryColor="#0078d4"
                initialSize={18}
                hoverSize={18}
                marginBottom="10px"
                onClick={(e) => this.resetRadioGroup(e)}
              />
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <RadioGroup
              data-testid={this.fieldName}
              id={this.fieldName}
              name={this.fieldName}
              data={this.state.options?.map((option) => ({ label: option, value: option })) ?? []}
              onChange={(e) => this.onChange(e)}
              onBlur={() => this.onBlur()}
              value={this.state.value}
              valid={this.isRequired ? this.state.valid : true}
              required={this.isRequired}
              disabled={this.isReadOnly || this.state.disabled || readOnlyEntry.value}
              layout={this.horizontalDisplay ? "horizontal" : "vertical"}
            />
            <If
              condition={
                this.fieldInformation !== undefined &&
                this.fieldInformation !== "" &&
                this.fieldInformation !== "<p></p>"
              }
            >
              <TooltipPopupIcon content={this.fieldInformation} />
            </If>
          </div>
          {!this.state.options && (
            <Label
              id={this.fieldName + "_label"}
              className="k-label"
            >
              No options available
            </Label>
          )}

          <If condition={this.hint}>
            <Hint>{this.hint}</Hint>
          </If>
          <If condition={!this.state.valid}>
            <Error>{this.validationMessage}</Error>
          </If>
        </div>
      </Ripple>
    );
  }
}
